import { useState, useEffect } from 'react';
import { Save, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { Button } from '@/components/ui/button';
import { EMPLOYEE_FIELD_REGISTRY, FIELD_CATEGORIES, isFieldEnabled } from '@/lib/employeeFields';

function Toggle({ on, onToggle, disabled }) {
  return (
    <button type="button" onClick={onToggle} disabled={disabled}
      className={`w-9 h-5 rounded-full transition-colors relative shrink-0 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${on ? 'bg-[var(--accent)]' : 'bg-[var(--border)]'}`}>
      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all ${on ? 'left-4' : 'left-0.5'}`}/>
    </button>
  );
}

/**
 * The actual field-toggle UI — shared between the full Settings page
 * (settings/employee-fields/page.jsx) and the in-context modal opened
 * from the Add/Edit Employee form, so both stay in sync with zero
 * duplicated logic.
 */
export function EmployeeFieldsEditor({ onSaved, compact = false }) {
  const { data, isLoading } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();

  const [toggles, setToggles] = useState({});

  useEffect(() => {
    const saved = data?.data?.settings?.employee_form_fields || [];
    const next = {};
    EMPLOYEE_FIELD_REGISTRY.forEach(f => {
      if (f.locked) return;
      next[f.key] = isFieldEnabled(f.key, saved);
    });
    setToggles(next);
  }, [data]);

  const handleToggle = (key) => setToggles(t => ({ ...t, [key]: !t[key] }));

  const handleSave = () => {
    const employee_form_fields = Object.entries(toggles).map(([key, enabled]) => ({ key, enabled }));
    mutate({ settings: { employee_form_fields } }, {
      onSuccess: () => { toast.success('Employee fields saved'); onSaved?.(); },
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className={compact ? 'space-y-3' : 'space-y-5'}>
      {isLoading && <div className="card text-center text-[13px] text-[var(--text-tertiary)] py-8">Loading…</div>}

      {!isLoading && FIELD_CATEGORIES.map(cat => {
        const fields = EMPLOYEE_FIELD_REGISTRY.filter(f => f.category === cat.key);
        if (!fields.length) return null;
        return (
          <div key={cat.key} className="card p-0 overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--border)]">
              <h3 className="text-[13px] font-bold text-[var(--text-primary)]">{cat.label}</h3>
            </div>
            <div className="divide-y divide-[var(--border)]">
              {fields.map(f => (
                <div key={f.key} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <p className="text-[13px] font-medium text-[var(--text-primary)] truncate">{f.label}</p>
                    {f.locked && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-[5px] bg-[var(--bg-muted)] text-[10px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)]">
                        <Lock size={9}/> Always required
                      </span>
                    )}
                  </div>
                  <Toggle on={f.locked ? true : !!toggles[f.key]} disabled={f.locked} onToggle={() => handleToggle(f.key)}/>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Button type="button" icon={Save} onClick={handleSave} disabled={isPending || isLoading}>{isPending ? 'Saving…' : 'Save Fields'}</Button>
    </div>
  );
}
